import { cn } from '@/lib/utils';
import {
  formatSlotTime,
  groupSlotsByPartOfDay,
  type SlotItem,
} from '@/scheduling/lib/slot-selection';

/**
 * The Morning / Afternoon / Evening slot strip — shared by the practitioner
 * rail and the Book-a-device rail (PROD-2455, "can we do them exactly like the
 * provider booking appointment?").
 *
 * Sharing the WHOLE list rather than exporting a bare `SlotGroup` is
 * deliberate: the grouping, the three labels, their order and the "hide an
 * empty bucket" rule are the part that has to stay identical between the two
 * rails, and a component that renders one group leaves each caller free to
 * drift on all four.
 *
 * ACCESSIBILITY — the one place this does NOT copy the practitioner rail's
 * markup, and the reason is a standing repo rule rather than a preference.
 * The practitioner rail rendered its chips as bare `<button>`s; the device
 * rail's chips have been `<label>`s wrapping a visually-hidden native radio
 * since the review #3 ruling (CLAUDE.md: "Custom radio-group rendered as
 * styled buttons" is a listed anti-pattern). Arrow-key navigation, the roving
 * tab stop, the checked state and the focus ring then come from the platform
 * instead of hand-rolled key handling. Picking one markup for both rails means
 * picking the accessible one, so the practitioner rail moves UP to radios
 * here. Visually and to a mouse nothing changes.
 */

interface SlotGroupListProps {
  /** Already filtered (past starts dropped) — see `toSlotItems`. */
  slots: readonly SlotItem[];
  /**
   * Radio-group `name`. Per-rail, so two rails can never share a group and
   * unselect each other's chip.
   */
  name: string;
  /** Accessible name of the fieldset wrapping every group. */
  legend: string;
  isSelected: (slot: SlotItem) => boolean;
  onPick: (slot: SlotItem) => void;
  /**
   * Optional suffix per chip — PROD-2526 marks the booking's OWN slot
   * (`· now`) when the device rail edits an existing booking. Absent, the
   * strip renders exactly as before, which is what the practitioner rail gets.
   */
  annotate?: (slot: SlotItem) => string | null;
}

const CHIP_BASE =
  'inline-flex cursor-pointer items-center h-10 px-3 rounded-full border text-sm font-medium ' +
  'transition-colors flex-shrink-0 ' +
  'has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-brand-primary has-[:focus-visible]:ring-offset-1';
const CHIP_ON = 'bg-brand-primary text-white border-brand-primary font-semibold';
const CHIP_OFF =
  'border-border-default bg-grey-100 text-text-primary hover:border-brand-primary hover:bg-blue-100';

export function SlotGroupList({ slots, name, legend, isSelected, onPick, annotate }: SlotGroupListProps) {
  const { morning, afternoon, evening } = groupSlotsByPartOfDay(slots);
  return (
    <fieldset className="space-y-4 border-0 p-0 m-0">
      <legend className="sr-only">{legend}</legend>
      {morning.length > 0 && (
        <SlotGroup label="Morning" slots={morning} name={name} isSelected={isSelected} onPick={onPick} annotate={annotate} />
      )}
      {afternoon.length > 0 && (
        <SlotGroup label="Afternoon" slots={afternoon} name={name} isSelected={isSelected} onPick={onPick} annotate={annotate} />
      )}
      {evening.length > 0 && (
        <SlotGroup label="Evening" slots={evening} name={name} isSelected={isSelected} onPick={onPick} annotate={annotate} />
      )}
    </fieldset>
  );
}

interface SlotGroupProps {
  label: string;
  slots: readonly SlotItem[];
  name: string;
  isSelected: (slot: SlotItem) => boolean;
  onPick: (slot: SlotItem) => void;
  annotate?: (slot: SlotItem) => string | null;
}

function SlotGroup({ label, slots, name, isSelected, onPick, annotate }: SlotGroupProps) {
  return (
    <div>
      <p className="text-xs font-medium text-text-muted mb-2">{label}</p>
      <div className="flex flex-wrap gap-2">
        {slots.map((s) => {
          const selected = isSelected(s);
          const note = annotate?.(s) ?? null;
          return (
            <label key={s.id} className={cn(CHIP_BASE, selected ? CHIP_ON : CHIP_OFF)}>
              <input
                type="radio"
                name={name}
                value={s.start}
                checked={selected}
                onChange={() => onPick(s)}
                className="sr-only"
              />
              {formatSlotTime(s.start)}
              {note ? <span className="ml-1 text-xs opacity-80">{note}</span> : null}
            </label>
          );
        })}
      </div>
    </div>
  );
}
